import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { inquiryAPI } from '../../services/api';
import PortalLayout from '../../components/PortalLayout';

interface Inquiry {
  id: number;
  title: string;
  description: string;
  category_name: string;
  budget: number;
  region: string;
  status: string;
  created_at: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  interested_count: number;
}

const CustomerInquiriesPage: React.FC = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const { showError } = useToast();

  const loadInquiries = useCallback(async () => {
    try {
      const response = await inquiryAPI.getMyInquiries();
      setInquiries(response.data.data || []);
    } catch (err: any) {
      showError('Fehler beim Laden der Anfragen');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const handleViewDetails = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInquiry(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'In Überprüfung';
      case 'approved':
        return 'Genehmigt';
      case 'rejected':
        return 'Abgelehnt';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Typography variant="h4" component="h1" mb={4}>
        Meine Anfragen
      </Typography>

      {inquiries.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary" mb={2}>
                Sie haben noch keine Anfragen eingereicht.
              </Typography>
              <Button variant="contained" href="/submit-inquiry">
                Erste Anfrage stellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Titel</TableCell>
                <TableCell>Kategorie</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Interessenten</TableCell>
                <TableCell>Eingereicht</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inquiries.map((inquiry) => (
                <TableRow key={inquiry.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{inquiry.title}</TableCell>
                  <TableCell>{inquiry.category_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(inquiry.status)}
                      color={getStatusColor(inquiry.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>€{inquiry.budget?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>{inquiry.interested_count || 0}</TableCell>
                  <TableCell>{new Date(inquiry.created_at).toLocaleDateString('de-DE')}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewDetails(inquiry)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Anfrage Details</DialogTitle>
        <DialogContent>
          {selectedInquiry && (
            <Box mt={2} display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Titel
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedInquiry.title}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={getStatusLabel(selectedInquiry.status)}
                  color={getStatusColor(selectedInquiry.status) as any}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Kategorie
                </Typography>
                <Typography variant="body1">{selectedInquiry.category_name}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Beschreibung
                </Typography>
                <Typography variant="body2">{selectedInquiry.description}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Budget
                </Typography>
                <Typography variant="body1">€{selectedInquiry.budget?.toFixed(2) || '0.00'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Region
                </Typography>
                <Typography variant="body1">{selectedInquiry.region || 'Nicht angegeben'}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Interessierte Auftragnehmer
                </Typography>
                <Typography variant="body1">{selectedInquiry.interested_count || 0}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Eingereicht am
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedInquiry.created_at).toLocaleDateString('de-DE')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </PortalLayout>
  );
};

export default CustomerInquiriesPage;
